'use client';

import React, { ElementType, useEffect, useRef } from 'react';

import { motion, useAnimation, useInView } from 'framer-motion';

interface BoxRevealProps {
    children: React.JSX.Element;
    width?: 'fit-content' | '100%';
    boxColor?: string;
    duration?: number;
    as?: ElementType;
}

export function BoxReveal({ children, width = 'fit-content', boxColor, duration, as = 'h2' }: BoxRevealProps) {
    const mainControls = useAnimation();
    const slideControls = useAnimation();

    const ref = useRef(null);
    const isInView = useInView(ref, { once: true });

    const MotionComponent = motion.create(as);

    useEffect(() => {
        if (isInView) {
            slideControls.start('visible');
            mainControls.start('visible');
        } else {
            slideControls.start('hidden');
            mainControls.start('hidden');
        }
    }, [isInView, mainControls, slideControls]);

    return (
        <div ref={ref} style={{ position: 'relative', width, overflow: 'hidden' }}>
            <MotionComponent
                variants={{
                    hidden: { opacity: 0, y: 75 },
                    visible: { opacity: 1, y: 0 },
                }}
                initial={isInView ? 'visible' : 'hidden'}
                animate={mainControls}
                transition={{ duration: duration || 0.5, delay: 0.25 }}
                className="leading-tight"
            >
                {children}
            </MotionComponent>

            <motion.div
                variants={{
                    hidden: { left: 0 },
                    visible: { left: '100%' },
                }}
                initial="hidden"
                animate={slideControls}
                transition={{ duration: duration || 0.5, ease: 'easeIn' }}
                style={{
                    position: 'absolute',
                    top: 4,
                    bottom: 4,
                    left: 0,
                    right: 0,
                    zIndex: 20,
                    background: boxColor || '#5046e6',
                }}
            />
        </div>
    );
}

export default BoxReveal;
